import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";


// Data for line chart
const salesData = [
  { month: "Jan", sales: 4000 },
  { month: "Feb", sales: 6000 },
  { month: "Mar", sales: 5500 },
  { month: "Apr", sales: 8000 },
  { month: "May", sales: 7000 },
  { month: "Jun", sales: 9000 },
];

// Data for pie chart
const orderStatus = [
  { name: "Completed", value: 65 },
  { name: "Pending", value: 25 },
  { name: "Canceled", value: 10 },
];
const pieColors = ["#cdfcd9", "#ffecb4ff", "#ff9088ff"];

// Dummy Orders
const orders = [
  { id: "#1234", date: "2025-08-10", status: "Completed", total: "$200" },
  { id: "#1235", date: "2025-08-15", status: "Pending", total: "$150" },
  { id: "#1236", date: "2025-08-18", status: "Canceled", total: "$300" },
];

const ViewCustomer = () => {
  const location = useLocation();
  const customer = location.state?.customer || { name: "Customer Name", email: "customer@email.com" };

  const stats = [
  { title: "Total Sales", value: customer.purchases, change: "+12%", color: "text-green-500" },
  { title: "Total Orders", value: customer.orderqty , change: "+8%", color: "text-green-500" },
  { title: "Total Transactions", value: "12,000", change: "+3%", color: "text-green-500" },
  { title: "Active Status", value: "Active", change: "", color: "text-green-500" },
];

  return (
    <div className="p-6 md:p-10">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">Customer Details</h1>
        <p className="text-gray-600 pb-6 text-sm flex flex-wrap gap-1 items-center dark:text-gray-400">
          <Link to="/dashboard" className="hover:underline">Dashboard</Link> /{" "}
          <Link to="/Users?selected=customers" className="hover:underline">Customers</Link> /{" "}
          <span className="agarly-gradient-text font-bold">{customer.name}</span>
        </p>
      </div>

      {/* Customer Info */}
      <div className="bg-white shadow-md rounded-2xl p-8 mb-6 dark:bg-[#1A1A1B] dark:border dark:border-[#3D3D3D]">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-200">Profile Info.</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-gray-500 text-sm dark:text-gray-300">Name</p>
            <p className="font-medium dark:text-gray-400">{customer.name}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm dark:text-gray-300">Email</p>
            <p className="font-medium dark:text-gray-400">{customer.email}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm dark:text-gray-300">Phone Number</p>
            <p className="font-medium dark:text-gray-400">{customer.phone}</p>
          </div>
          <div>
            <p className="text-gray-500 text-sm dark:text-gray-300">Address</p>
            <p className="font-medium dark:text-gray-400">{customer.address}</p>
          </div>
          
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 rounded-2xl">
        {stats.map((item, i) => (
          <div
            key={i}
            className={`p-4 rounded-xl hover:-translate-y-1 shadow-md transition
              ${i === 0 ? "agarly-btn-primary text-agarly-dark" : "bg-white text-gray-800 dark:bg-[#1A1A1B] dark:border dark:border-[#3D3D3D]"}
            `}
          >
            <p className={`text-base font-medium pb-6 ${i === 0 ? "text-gray-500" : "text-gray-500 dark:text-gray-300"}`}>{item.title}</p>
            <h3 className={`text-3xl font-extrabold ${i === 0 ? "text-white" : "agarly-gradient-text"}`}>{item.value}</h3>
            {item.change && (
              <span className={`${item.color} text-xs ${i === 0 ? "text-green-300" : ""}`}>
                {item.change}
              </span>
            )}
          </div>
        ))}
      </div>


      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Sales Line Chart */}
        <div className="bg-white p-4 rounded-2xl shadow dark:bg-[#1A1A1B] dark:border dark:border-[#3D3D3D]">
          <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">Sales Over Time</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={salesData}>
              <CartesianGrid strokeDasharray="3 3" className="dark:stroke-gray-700"/>
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="sales" stroke="#92bfff" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Orders Status Pie */}
        <div className="bg-white p-4 rounded-2xl shadow dark:bg-[#1A1A1B] dark:border dark:border-[#3D3D3D]">
          <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">Order Status</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={orderStatus} dataKey="value" outerRadius={100} label>
                {orderStatus.map((entry, index) => (
                  <Cell key={index} fill={pieColors[index % pieColors.length]} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white p-4 rounded-2xl shadow dark:bg-[#1A1A1B] dark:border dark:border-[#3D3D3D]">
        <h2 className="text-lg font-semibold mb-4 dark:text-gray-200">Recent Orders</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b dark:text-gray-300">
              <th className="py-2">Order ID</th>
              <th>Date</th>
              <th>Status</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, i) => (
              <tr key={i} className="border-b hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#2D2D2D] dark:border-[#3D3D3D]">
                <td className="py-4">{order.id}</td>
                <td>{order.date}</td>
                <td>
                  <span
                    className={`px-2 py-2 rounded-full text-xs ${
                      order.status === "Completed"
                        ? "bg-green-100 text-green-600"
                        : order.status === "Pending"
                        ? "bg-yellow-100 text-yellow-600"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {order.status}
                  </span>
                </td>
                <td>{order.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewCustomer;
